import React, { useEffect, useState } from 'react';
import AdminLayout from './components/adminlayout';
import { serverURL } from '../constants';
import axios from 'axios';
import { toast } from 'react-toastify';

const Settings = () => {
    const [settings, setSettings] = useState({
        siteName: '', companyName: '', logoUrl: '', heroTitle: '', heroSubtitle: '',
        smtpHost: '', smtpPort: '', smtpEmail: '', smtpPassword: '',
        currency: 'USD', currencySymbol: '$',
        googleClientId: '', facebookClientId: '',
        aiProvider: 'gemini', geminiApiKey: '', openaiApiKey: '', claudeApiKey: '', aiModel: ''
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        sessionStorage.setItem('darkMode', false);
        fetchSettings();
    }, []);

    async function fetchSettings() {
        try {
            const response = await axios.get(serverURL + '/api/getsettings');
            if (response.data) setSettings(prev => ({ ...prev, ...response.data }));
        } catch (error) {
            console.error(error);
        }
    }

    async function saveSettings() {
        setSaving(true);
        try {
            await axios.post(serverURL + '/api/savesettings', settings);
            toast.success('Settings saved successfully');
        } catch (error) {
            toast.error('Error saving settings');
        }
        setSaving(false);
    }

    const Notice = ({ type = 'info', children }) => {
        const styles = {
            info: 'bg-blue-50 border-blue-200 text-blue-800',
            warning: 'bg-amber-50 border-amber-200 text-amber-800',
            success: 'bg-green-50 border-green-200 text-green-800',
        };
        const icons = { info: 'i', warning: '!', success: '✓' };
        return (
            <div className={`${styles[type]} border rounded-lg p-3 text-sm flex items-start gap-2 mb-4`}>
                <span className='font-bold text-lg leading-none'>{icons[type]}</span>
                <div>{children}</div>
            </div>
        );
    };

    const Section = ({ title, description, children, notice }) => (
        <div className='bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden'>
            <div className='px-6 py-4 border-b border-gray-100 bg-gray-50'>
                <h3 className='font-bold text-base'>{title}</h3>
                {description && <p className='text-sm text-gray-500 mt-0.5'>{description}</p>}
            </div>
            <div className='p-6'>
                {notice && notice}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>{children}</div>
            </div>
        </div>
    );

    const Input = ({ label, field, type = 'text', placeholder = '', hint = '', fullWidth = false }) => (
        <div className={fullWidth ? 'md:col-span-2' : ''}>
            <label className='text-sm font-medium text-gray-700 block mb-1.5'>{label}</label>
            <input
                className='border border-gray-300 rounded-lg w-full px-3 py-2.5 text-sm focus:ring-2 focus:ring-black focus:border-black transition-shadow'
                type={type}
                value={settings[field] || ''}
                onChange={e => setSettings({ ...settings, [field]: e.target.value })}
                placeholder={placeholder}
            />
            {hint && <p className='text-xs text-gray-400 mt-1'>{hint}</p>}
        </div>
    );

    const Select = ({ label, field, options, hint = '' }) => (
        <div>
            <label className='text-sm font-medium text-gray-700 block mb-1.5'>{label}</label>
            <select
                className='border border-gray-300 rounded-lg w-full px-3 py-2.5 text-sm focus:ring-2 focus:ring-black focus:border-black'
                value={settings[field] || ''}
                onChange={e => setSettings({ ...settings, [field]: e.target.value })}
            >
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {hint && <p className='text-xs text-gray-400 mt-1'>{hint}</p>}
        </div>
    );

    return (
        <AdminLayout>
            <div className='p-6 max-w-4xl'>
                <div className='flex justify-between items-center mb-6'>
                    <div>
                        <h2 className='text-xl font-bold'>Settings</h2>
                        <p className='text-sm text-gray-500'>Configure your platform. Changes are saved to the database.</p>
                    </div>
                    <button onClick={saveSettings} disabled={saving} className='bg-black text-white px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-800 transition-colors disabled:opacity-50'>
                        {saving ? 'Saving...' : 'Save All'}
                    </button>
                </div>

                <Section title="General" description="Basic platform information">
                    <Input label="Site Name" field="siteName" placeholder="Coursea" />
                    <Input label="Company Name" field="companyName" placeholder="Coursea Inc." />
                    <Input label="Logo URL" field="logoUrl" placeholder="https://example.com/logo.png" hint="Direct link to your logo image" />
                    <Select label="Currency" field="currency" options={[
                        { value: 'USD', label: 'USD ($)' }, { value: 'EUR', label: 'EUR' },
                        { value: 'GBP', label: 'GBP' }, { value: 'RON', label: 'RON (lei)' }
                    ]} />
                </Section>

                <Section title="Homepage" description="Customize the landing page hero section">
                    <Input label="Hero Title" field="heroTitle" placeholder="Learn Anything with AI" hint="Main heading on the landing page" />
                    <Input label="Hero Subtitle" field="heroSubtitle" placeholder="Generate courses on any topic instantly" hint="Subtext below the main heading" />
                </Section>

                <Section title="AI Provider" description="Choose which AI generates your courses"
                    notice={
                        <Notice type="warning">
                            <strong>Security recommendation:</strong> For production, store API keys in Railway environment variables instead of here.
                            Go to <strong>Railway → Variables</strong> and add <code>API_KEY=your_key</code>.
                            Keys saved here work too but are stored in the database.
                            See the <strong>Guide</strong> page for detailed instructions.
                        </Notice>
                    }
                >
                    <Select label="Provider" field="aiProvider" options={[
                        { value: 'gemini', label: 'Google Gemini' },
                        { value: 'openai', label: 'OpenAI (GPT-4, GPT-4o)' },
                        { value: 'claude', label: 'Claude (Anthropic)' }
                    ]} hint="Select the AI that will generate course content" />
                    <Input label="Model (optional)" field="aiModel" placeholder="Leave empty for default" hint="e.g. gpt-4o-mini, claude-sonnet-4-20250514, gemini-1.5-flash" />
                    <Input label="Gemini API Key" field="geminiApiKey" type="password" placeholder="AIza..." hint="Get from ai.google.dev" />
                    <Input label="OpenAI API Key" field="openaiApiKey" type="password" placeholder="sk-..." hint="Get from platform.openai.com/api-keys" />
                    <Input label="Claude API Key" field="claudeApiKey" type="password" placeholder="sk-ant-..." hint="Get from console.anthropic.com" />
                </Section>

                <Section title="Email (SMTP)" description="For password resets and subscription notifications"
                    notice={
                        <Notice type="info">
                            For Gmail: use <strong>smtp.gmail.com</strong> port <strong>465</strong> and generate an
                            <strong> App Password</strong> at myaccount.google.com → Security → 2-Step Verification → App Passwords.
                        </Notice>
                    }
                >
                    <Input label="SMTP Host" field="smtpHost" placeholder="smtp.gmail.com" />
                    <Input label="SMTP Port" field="smtpPort" placeholder="465" />
                    <Input label="Email Address" field="smtpEmail" placeholder="your@email.com" hint="Sender email address" />
                    <Input label="Email Password" field="smtpPassword" type="password" placeholder="App password" hint="NOT your Gmail password — use an App Password" />
                </Section>

                <Section title="Social Login" description="OAuth credentials for Google and Facebook sign-in"
                    notice={
                        <Notice type="info">
                            Add your Railway domain as an authorized redirect URI in each provider's console.
                        </Notice>
                    }
                >
                    <Input label="Google Client ID" field="googleClientId" placeholder="xxx.apps.googleusercontent.com" hint="Google Cloud Console → APIs → Credentials" />
                    <Input label="Facebook App ID" field="facebookClientId" placeholder="123456789" hint="Facebook Developer Portal → Your App → Settings" />
                </Section>

                <Section title="Payment Gateways" description="API keys for payment processors"
                    notice={
                        <Notice type="warning">
                            <strong>Security:</strong> Secret keys should be stored in <strong>Railway environment variables</strong>.
                            Go to Railway → Variables → Raw Editor and paste:<br/>
                            <code>STRIPE_SECRET_KEY=sk_...</code><br/>
                            <code>PAYPAL_CLIENT_ID=...</code><br/>
                            <code>PAYPAL_APP_SECRET_KEY=...</code><br/>
                            <code>RAZORPAY_KEY_ID=...</code><br/>
                            <code>RAZORPAY_KEY_SECRET=...</code><br/>
                            <code>PAYSTACK_SECRET_KEY=...</code><br/>
                            <code>FLUTTERWAVE_PUBLIC_KEY=...</code><br/>
                            <code>FLUTTERWAVE_SECRET_KEY=...</code><br/>
                            <br/>Plan IDs (price_..., P-...) are set in <strong>Plans</strong> page per plan.
                        </Notice>
                    }
                >
                </Section>

                <div className='flex justify-end mt-2 mb-8'>
                    <button onClick={saveSettings} disabled={saving} className='bg-black text-white px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-800 transition-colors disabled:opacity-50'>
                        {saving ? 'Saving...' : 'Save All Settings'}
                    </button>
                </div>
            </div>
        </AdminLayout>
    );
};
export default Settings;
